import { Fragment, type ReactElement, useCallback } from 'react';

import { components, type GroupBase, type MultiValueProps } from 'react-select';

import classNames from 'classnames';

import { isDefined, isNotDefined } from '@enterprise/core';

import Hidden from '#select/components/hidden.component.tsx';

export const useMultiValueConfiguration = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>({
  children,
  selectProps,
  ...props
}: MultiValueProps<Option, IsMulti, Group>) => {
  const { components: multiValueComponents, data, ...newProps } = props;
  const { isFixed, maxVisible } = selectProps;
  const { getValue, index } = newProps;

  const isItemFixed = useCallback(
    (value: Group | Option) => {
      return isFixed?.(value) ?? false;
    },
    [isFixed]
  );

  const hidden = useCallback(() => {
    const length = getValue().length;

    if (isNotDefined(maxVisible)) {
      return 0;
    }

    return Math.max(length - maxVisible, 0);
  }, [getValue, maxVisible]);

  const DefaultComponent = (
    <components.MultiValue
      {...newProps}
      className={classNames({ 'hsp-select-is-fixed': isItemFixed(data) }, props.className)}
      components={{
        ...multiValueComponents,
        Remove: (multiValueRemoveProps) => {
          return isItemFixed(data) ? <Fragment /> : <multiValueComponents.Remove {...multiValueRemoveProps} />;
        },
      }}
      data={data}
      selectProps={selectProps}>
      {children}
    </components.MultiValue>
  );
  let Component: ReactElement | null = DefaultComponent;

  if (isDefined(maxVisible)) {
    if (index < maxVisible) {
      Component = DefaultComponent;
    } else if (index === maxVisible) {
      Component = <Hidden count={hidden()} />;
    } else {
      Component = null;
    }
  }

  return {
    Component,
  };
};
